import React, { useState, useEffect, useContext, createContext } from 'react';

import { Menu, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ArrowRight, Mail, MapPin } from 'lucide-react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { T } from './i18n';

// ── Copy context (English only for now; structured so more languages can return) ──
const LangContext = createContext({ t: T.en });
const useLang = () => useContext(LangContext);

// ── Colours ───────────────────────────────────────────────────────────────────
const b = {
  deepCove:   '#335860',  // deep teal — buttons, footer, dark cards, eyebrows on white
  tidal:      '#6CB1B8',  // teal — accents, icons, dots, italic emphasis
  sage:       '#D5DCC2',  // soft green — alternating section backgrounds
  peach:      '#F4BF98',  // warm accent — labels/buttons on dark backgrounds
  warm:       '#C6874C',  // deep peach accent — eyebrows/emphasis on CREAM sections
  firstLight: '#F7F3E2',  // cream — warm CTA bands, light text on dark
  stone:      '#4E6A70',  // muted teal — body text (readable on light & cream)
  nearBlack:  '#22383D',  // near-black teal — headings, strong text
  white:      '#FFFFFF',
};

// ── Constants ─────────────────────────────────────────────────────────────────
const HERO_IMG    = 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=2400&auto=format&fit=crop&q=85';
const PORTRAIT_IMG = `${process.env.PUBLIC_URL}/portrait.jpg`;
const HOME_PORTRAIT = `${process.env.PUBLIC_URL}/portrait-home.jpg`;
const ABOUT_PORTRAIT = `${process.env.PUBLIC_URL}/portrait-about.jpg`;
const QUIET_IMG = `${process.env.PUBLIC_URL}/quiet-moment.jpg`;
const CONSULT     = 'https://calendar.app.google/7Whomn82yUd8LB9Z9';
const AMINDSET_MAP = 'https://www.google.com/maps/search/?api=1&query=AMindset+Psychology+Services+LKF+Tower+33+Wyndham+Street+Central+Hong+Kong';
const SESSION     = 'mailto:hello@thecovecounselling.com?subject=Hi%20Lindsey%2C%20I%27d%20like%20to%20begin%20therapy&body=Hi%20Lindsey%2C%0D%0A%0D%0AI%20think%20I%27m%20ready%20to%20begin%20therapy%2C%20and%20I%27d%20love%20to%20hear%20what%20it%20involves%2C%20including%20fees%2C%20availability%2C%20and%20next%20steps.%0D%0A%0D%0AA%20little%20about%20me%20and%20what%27s%20brought%20me%20here%3A%0D%0A%0D%0A';
const SLIDING     = 'mailto:hello@thecovecounselling.com?subject=Hi%20Lindsey%2C%20I%27d%20like%20to%20ask%20about%20a%20sliding-scale%20place&body=Hi%20Lindsey%2C%0D%0A%0D%0AI%27m%20hoping%20to%20begin%20therapy%20with%20you%2C%20and%20I%20wanted%20to%20ask%20whether%20one%20of%20your%20sliding-scale%20places%20might%20be%20possible.%20The%20standard%20fee%20is%20more%20than%20I%20can%20comfortably%20manage%20at%20the%20moment%2C%20and%20being%20able%20to%20start%20would%20mean%20a%20lot%20to%20me.%0D%0A%0D%0AIf%20it%20helps%2C%20I%20can%20share%20a%20little%20about%20my%20circumstances%2C%20what%20would%20feel%20manageable%20for%20me%2C%20and%20something%20of%20what%20has%20brought%20me%20here%3A%0D%0A%0D%0A';

const NAV = [
  { id: 'home',     label: 'Home'       },
  { id: 'about',    label: 'About'      },
  { id: 'services', label: 'Services'   },
  { id: 'process',  label: 'The Process'},
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'contact',  label: 'Contact'    },
  { id: 'faq',      label: 'FAQ'        },
];

// ── Routing + per-page SEO metadata ─────────────────────────────────────────────
const SITE_URL = 'https://thecovecounselling.com';
const PATHS = {
  home: '/', about: '/about', services: '/services', process: '/process',
  testimonials: '/testimonials', faq: '/faq', contact: '/contact',
  privacy: '/privacy', emergency: '/emergency',
};
const pathFor = (id) => PATHS[id] || '/';
const idForPath = (p) => Object.keys(PATHS).find(k => PATHS[k] === p) || 'home';

// Fire a GA4 event (gtag is loaded in index.html). Safe no-op if analytics is blocked.
const track = (name, params) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') window.gtag('event', name, params || {});
};

const META = {
  home:        { title: 'The Cove | Counselling for Cross-Cultural Professionals in Hong Kong', description: 'Individual counselling in English and Chinese for high-achieving, bicultural professionals in Hong Kong and online. For burnout, identity, belonging, and the exhaustion of holding it all together. With Lindsey Sun.' },
  about:       { title: 'The Cove | About Lindsey Sun, Cross-Cultural Counsellor in Hong Kong', description: 'Born and raised in mainland China, US-educated, Hong Kong-based. A client-centred counsellor working in English and Chinese with professionals navigating identity, culture, and high-performance pressure. ACA, PACFA.' },
  services:    { title: 'The Cove | Counselling for Burnout, Identity & Cross-Cultural Life, Hong Kong', description: 'Individual counselling in English and Chinese for burnout, high-functioning anxiety, cross-cultural identity, relationships, and life transitions. LGBTQ+ and GSRD-affirming. Hong Kong and online.' },
  process:     { title: 'The Cove | Fees & What to Expect', description: 'Session fees at The Cove, including sliding-scale places, and what working together looks like, from an optional free conversation to ongoing sessions. Counselling in Hong Kong and online.' },
  testimonials:{ title: 'The Cove | Client Testimonials', description: 'In their words, reflections clients have kindly shared about individual counselling with Lindsey Sun at The Cove, Hong Kong.' },
  faq:         { title: 'The Cove | Counselling FAQ', description: 'Questions about counselling at The Cove: sessions in English and Chinese, working across cultures, fees, confidentiality, LGBTQ+ and GSRD-affirming care, and what to expect.' },
  contact:     { title: 'The Cove | Contact & Book a Free Conversation, Hong Kong', description: 'Get in touch with The Cove Counselling, Central Hong Kong. A free 15-minute conversation, in English or Mandarin, online or in person. Counselling for cross-cultural professionals.' },
  privacy:     { title: 'The Cove | Privacy Policy', description: 'How The Cove Counselling handles your personal information and confidentiality.' },
  emergency:   { title: 'The Cove | Emergency Resources, Hong Kong', description: 'Crisis and emergency mental-health resources in Hong Kong. The Cove is not an emergency service. If you are in danger, call 999.' },
};

// React 19 hoists <title>/<meta>/<link> rendered here into <head>, per route.
function Seo({ id }) {
  const m = META[id] || META.home;
  const url = SITE_URL + pathFor(id);
  return (
    <>
      <title>{m.title}</title>
      <meta name="description" content={m.description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={m.title} />
      <meta property="og:description" content={m.description} />
      <meta property="og:url" content={url} />
    </>
  );
}

// WhatsApp glyph (lucide-react has no brand icons)
const WhatsAppIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={color} style={{ flexShrink: 0 }} aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.599 5.367l-.999 3.648 3.889-1.02zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
  </svg>
);

// Instagram glyph (lucide-react has no brand icons)
const InstagramIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.9" style={{ flexShrink: 0 }} aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5.2" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.3" cy="6.7" r="1.1" fill={color} stroke="none" />
  </svg>
);

// WeChat glyph (lucide-react has no brand icons) — two speech bubbles, outline to match
const WeChatIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
    <path d="M9 3.5C13 3.5 16 6 16 9 16 12 13 14.5 9 14.5 8.2 14.5 7.5 14.4 6.8 14.2L4 15.8 4.8 13.2C3 12 2 10.6 2 9 2 6 5 3.5 9 3.5Z" />
    <path d="M17 10.5C20.2 10.5 22.8 12.5 22.8 14.9 22.8 16.3 22 17.5 20.7 18.4L21.3 20.4 19 19.2C18.3 19.4 17.7 19.5 17 19.5 13.8 19.5 11.2 17.5 11.2 15 11.2 12.5 13.8 10.5 17 10.5Z" />
    <circle cx="6.5" cy="7.2" r=".95" fill={color} stroke="none" />
    <circle cx="10.1" cy="7.2" r=".95" fill={color} stroke="none" />
    <circle cx="14.7" cy="12.6" r=".85" fill={color} stroke="none" />
    <circle cx="18.5" cy="12.6" r=".85" fill={color} stroke="none" />
  </svg>
);

const WECHAT_QR = `${process.env.PUBLIC_URL}/wechat-qr.png`;

// WeChat contact row: shows the add-me QR in a small card on hover (and on tap)
function WeChatContact({ iconColor, textColor, iconSize = 14, gap = 8 }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: `${gap}px`, cursor: 'pointer', color: textColor }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={() => setOpen((o) => !o)}
    >
      <WeChatIcon size={iconSize} color={iconColor} />
      <span>@lindsey_CoveHK</span>
      {open && (
        <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '12px', zIndex: 60, backgroundColor: '#fff', borderRadius: '16px', padding: '12px', boxShadow: '0 16px 44px rgba(34,56,61,0.30)', width: '188px', pointerEvents: 'none' }}>
          <img src={WECHAT_QR} alt="Scan the QR code to add Lindsey on WeChat" style={{ width: '100%', display: 'block', borderRadius: '8px' }} />
        </div>
      )}
    </div>
  );
}

// Tracks whether the viewport is narrow enough to switch to the mobile layout
// (stacked grids + hamburger nav). Inline styles can't use CSS media queries,
// so we read the width in JS and re-render on resize.
function useIsNarrow(breakpoint = 820) {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

// Renders a plain string with **phrases** wrapped as semibold emphasis anchors,
// for scannable key facts in data-driven copy (light backgrounds).
function withEmphasis(text) {
  return String(text).split('**').map((seg, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ fontWeight: 600, color: b.nearBlack }}>{seg}</strong>
      : <React.Fragment key={i}>{seg}</React.Fragment>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar({ page, scrolled }) {
  const [open, setOpen] = useState(false);
  const narrow = useIsNarrow();
  const { t } = useLang();

  // Light (white) text only when sitting over the dark hero — i.e. the top of
  // the Home page. Every other page starts on a cream background, so the navbar
  // must use its solid/dark treatment there or the links become invisible.
  // The About page also opens on a dark, full-bleed band (the philosophy note),
  // so the navbar goes transparent/white over it too — like the home hero.
  const overHero = (page === 'home' || page === 'about') && !scrolled;

  // Over the dark band we use white text — but only while the mobile menu is
  // closed. Opening it turns the bar solid cream, so text must go dark to read.
  const light = overHero && !open;

  // The "Contact" button (below) is the single entry to the contact page, so
  // 'contact' is filtered out of the text links to avoid a duplicate item.
  const links = NAV.filter(l => l.id !== 'contact' && l.id !== 'faq');

  return (
    <nav style={{
      backgroundColor: (overHero && !open) ? 'transparent' : b.firstLight,
      boxShadow: (scrolled && !overHero) ? '0 6px 24px rgba(51,88,96,0.09)' : 'none',
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
      transition: 'background-color 0.4s ease, box-shadow 0.4s ease',
    }}>
      <div style={{
        maxWidth: '1024px', margin: '0 auto', padding: '20px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        {/* Logo */}
        <Link to="/" aria-label="The Cove Counselling — home"
          style={{ display: 'inline-block', lineHeight: 0, textDecoration: 'none' }}>
          <img
            src={light ? '/logo-wordmark-beige.png' : '/logo-wordmark-teal.png'}
            alt="The Cove Counselling"
            style={{ height: narrow ? '40px' : '48px', width: 'auto', display: 'block' }}
          />
        </Link>

        {/* Desktop links */}
        <div style={{ display: narrow ? 'none' : 'flex', alignItems: 'center', gap: '24px' }}>
          {links.map(l => (
            <Link key={l.id} to={pathFor(l.id)} onClick={() => setOpen(false)}
              style={{
                fontSize: '14px', whiteSpace: 'nowrap', textDecoration: 'none',
                color: light ? b.white : (page === l.id ? b.deepCove : b.stone),
                fontWeight: light ? 300 : (page === l.id ? 500 : 400),
              }}>
              {t.nav[l.id]}
            </Link>
          ))}
          <Link to="/contact" style={{
            fontSize: '14px', padding: '8px 20px', borderRadius: '999px',
            backgroundColor: b.deepCove, color: b.white, textDecoration: 'none',
            whiteSpace: 'nowrap'
          }}>
            {t.nav.contact}
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setOpen(o => !o)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          style={{ display: narrow ? 'inline-flex' : 'none', alignItems: 'center', justifyContent: 'center', padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: light ? b.white : b.deepCove }}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && narrow && (
        <div style={{ padding: '4px 24px 20px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid rgba(51,88,96,0.08)' }}>
          {links.map(l => (
            <Link key={l.id} to={pathFor(l.id)} onClick={() => setOpen(false)}
              style={{ fontSize: '16px', textAlign: 'left', textDecoration: 'none', color: page === l.id ? b.deepCove : b.nearBlack, fontWeight: page === l.id ? 500 : 400, padding: '6px 0' }}>
              {t.nav[l.id]}
            </Link>
          ))}
          <Link to="/contact" onClick={() => setOpen(false)} style={{
            fontSize: '14px', padding: '10px 24px', borderRadius: '999px',
            backgroundColor: b.deepCove, color: b.white, textDecoration: 'none',
            width: 'fit-content'
          }}>
            {t.nav.contact}
          </Link>
        </div>
      )}
    </nav>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const narrow = useIsNarrow();
  const { t } = useLang();
  return (
    <footer style={{ backgroundColor: b.deepCove, padding: '48px 0 0', position: 'relative' }}>
      {/* Wavy top edge — the deep teal rising into the section above, bookending the hero wave */}
      <svg viewBox="0 0 1440 72" preserveAspectRatio="none" aria-hidden="true"
        style={{ position: 'absolute', left: 0, right: 0, top: -71, width: '100%', height: '72px', display: 'block' }}>
        <path fill={b.deepCove} d="M0,10 C480,20 960,66 1440,36 L1440,72 L0,72 Z" />
      </svg>

      <div style={{
        maxWidth: '1024px', margin: '0 auto', padding: '0 24px',
        display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1.1fr 0.8fr 1.1fr', gap: narrow ? '36px' : '40px', marginBottom: '36px'
      }}>
        {/* Brand */}
        <div>
          <img
            src="/logo-wordmark-beige.png"
            alt="The Cove Counselling"
            style={{ height: '58px', width: 'auto', display: 'block', marginBottom: '20px' }}
          />
          <p style={{ color: 'rgba(247,243,226,0.4)', fontSize: '14px', letterSpacing: '0.02em', margin: 0 }}>
            {t.common.langsLine}
          </p>
          <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid rgba(247,243,226,0.12)', maxWidth: '320px' }}>
            <p style={{ color: b.peach, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', margin: '0 0 8px' }}>
              {t.footer.crisisHeading}
            </p>
            <p style={{ color: 'rgba(247,243,226,0.7)', fontSize: '14px', lineHeight: 1.7, margin: 0 }}>
              {t.footer.crisisPre}
              <Link to="/emergency" style={{ color: b.peach, fontSize: '14px', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                {t.footer.crisisLink}
              </Link>{t.footer.crisisPost}
            </p>
          </div>
        </div>

        {/* Nav */}
        <div>
          <p style={{ color: b.peach, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', margin: '0 0 20px' }}>
            {t.footer.explore}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {NAV.filter(l => l.id !== 'home').map(l => (
              <Link key={l.id} to={pathFor(l.id)}
                style={{ fontSize: '14px', color: 'rgba(247,243,226,0.7)', textDecoration: 'none', textAlign: 'left', width: 'fit-content' }}>
                {l.id === 'testimonials' ? t.footer.testimonials : t.nav[l.id]}
              </Link>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div>
          <p style={{ color: b.peach, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', margin: '0 0 20px' }}>
            {t.footer.contactHeading}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: 'rgba(247,243,226,0.7)', lineHeight: 1.7 }}>
            <a href="mailto:hello@thecovecounselling.com"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(247,243,226,0.7)', textDecoration: 'none' }}>
              <Mail size={14} style={{ color: b.peach, flexShrink: 0 }} />
              hello@thecovecounselling.com
            </a>
            <a href="https://wa.link/8sd6o4" target="_blank" rel="noopener noreferrer" onClick={() => track('whatsapp_click')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(247,243,226,0.7)', textDecoration: 'none' }}>
              <WhatsAppIcon size={15} color={b.peach} />
              +852 9065 1097
            </a>
            <a href="https://instagram.com/sitwithlindsey" target="_blank" rel="noopener noreferrer" onClick={() => track('instagram_click')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(247,243,226,0.7)', textDecoration: 'none' }}>
              <InstagramIcon size={14} color={b.peach} />
              @sitwithlindsey
            </a>
            <WeChatContact iconColor={b.peach} textColor="rgba(247,243,226,0.7)" iconSize={15} />
            <a href={AMINDSET_MAP} target="_blank" rel="noopener noreferrer" onClick={() => track('map_click')}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: 'rgba(247,243,226,0.7)', textDecoration: 'none', lineHeight: 1.65 }}>
              <MapPin size={14} style={{ color: b.peach, flexShrink: 0, marginTop: '3px' }} />
              <span>
                <span style={{ display: 'block', color: 'rgba(247,243,226,0.92)' }}>AMindset Psychology Services</span>
                Suites 2116 &amp; 2317, LKF Tower<br />
                33 Wyndham Street, Central, Hong&nbsp;Kong
              </span>
            </a>
          </div>
        </div>
      </div>

      <div style={{
        maxWidth: '1024px', margin: '0 auto', padding: '22px 24px 30px',
        borderTop: '1px solid rgba(247,243,226,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <span style={{ fontSize: '14px', color: 'rgba(247,243,226,0.4)' }}>{t.footer.rights}</span>
          <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: 'rgba(247,243,226,0.4)' }}>
            <Link to="/privacy" style={{ color: 'rgba(247,243,226,0.4)', textDecoration: 'none' }}>{t.footer.privacy}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── Home ──────────────────────────────────────────────────────────────────────
// Quiet contextual testimonial for individual pages (links to the full Testimonials page)
function PullQuote({ quote, initials, light, style }) {
  const quoteColor = light ? b.firstLight : b.deepCove;
  const initialsColor = light ? 'rgba(247,243,226,0.8)' : b.stone;
  return (
    <div style={{ borderLeft: `3px solid ${b.tidal}`, borderRadius: '0 14px 14px 0', backgroundColor: light ? 'rgba(247,243,226,0.08)' : 'rgba(213,220,194,0.38)', padding: '20px 24px', maxWidth: '520px', ...style }}>
      <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 400, color: quoteColor, fontSize: '20px', lineHeight: 1.5, margin: '0 0 14px' }}>
        &ldquo;{quote}&rdquo;
      </p>
      <p style={{ color: initialsColor, fontSize: '12px', letterSpacing: '0.16em', fontWeight: 600, margin: 0 }}>
        <span style={{ color: b.warm }}>&mdash;</span>&nbsp;{initials}
      </p>
    </div>
  );
}

function TestimonialCarousel({ items, labels, go }) {
  const [i, setI] = useState(0);
  const narrow = useIsNarrow();
  const item = items[i];
  const move = (d) => setI(n => (n + d + items.length) % items.length);
  useEffect(() => {
    if (items.length <= 1) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = setInterval(() => setI(n => (n + 1) % items.length), 6500);
    return () => clearInterval(id);
  }, [i, items.length]);
  const arrowBtn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '42px', height: '42px', borderRadius: '50%',
    border: '1px solid rgba(51,88,96,0.3)', backgroundColor: 'transparent',
    color: b.deepCove, cursor: 'pointer', flexShrink: 0
  };
  return (
    <div>
      <div key={i} className="tst-fade">
        <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center', gap: narrow ? '10px' : '20px', maxWidth: '760px', margin: '0 auto' }}>
          <span aria-hidden="true" style={{ fontFamily: 'var(--serif)', fontSize: narrow ? '48px' : '72px', lineHeight: 1, color: b.deepCove, opacity: 0.28, flexShrink: 0, alignSelf: 'flex-start' }}>&ldquo;</span>
          <blockquote style={{ margin: 0, minHeight: narrow ? 'auto' : '112px', display: 'flex', alignItems: 'center' }}>
            <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 400, color: b.deepCove, fontSize: 'clamp(28px,4.5vw,40px)', lineHeight: 1.4, margin: 0 }}>
              {item.highlight}
            </p>
          </blockquote>
          <span aria-hidden="true" style={{ fontFamily: 'var(--serif)', fontSize: narrow ? '48px' : '72px', lineHeight: 1, color: b.deepCove, opacity: 0.28, flexShrink: 0, alignSelf: 'flex-end' }}>&rdquo;</span>
        </div>
        <p style={{ color: b.warm, fontSize: '12px', letterSpacing: '0.22em', fontWeight: 600, margin: '24px 0 0' }}>
          {item.initials}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginTop: '36px' }}>
        <button onClick={() => move(-1)} aria-label="Previous testimonial" style={arrowBtn}>
          <ChevronLeft size={18} />
        </button>
        <div style={{ display: 'flex', gap: '10px' }}>
          {items.map((_, n) => (
            <button key={n} onClick={() => setI(n)} aria-label={`Show testimonial ${n + 1}`} style={{
              width: '8px', height: '8px', borderRadius: '50%', padding: 0, cursor: 'pointer', border: 'none',
              backgroundColor: n === i ? b.deepCove : 'rgba(51,88,96,0.25)', transition: 'background-color 0.3s ease'
            }} />
          ))}
        </div>
        <button onClick={() => move(1)} aria-label="Next testimonial" style={arrowBtn}>
          <ChevronRight size={18} />
        </button>
      </div>
      <button onClick={() => go('testimonials')} className="cta-link" style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '28px',
        color: b.deepCove, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase',
        background: 'none', border: 'none', cursor: 'pointer', padding: 0
      }}>
        <span style={{ textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationThickness: '1px' }}>{labels.linkLabel}</span>
        <ArrowRight size={13} className="cta-arrow" />
      </button>
    </div>
  );
}

// ── Testimonials (full stories page) ────────────────────────────────────────────
function Testimonials({ go }) {
  const { t } = useLang();
  const narrow = useIsNarrow();
  const ts = t.home.testimonials;
  return (
    <section style={{ backgroundColor: b.white, paddingTop: '128px', paddingBottom: '96px' }}>
      <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
        <h1 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(40px,5vw,52px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '24px' }}>
          {ts.pageHeadingPre}<span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.tidal }}>{ts.pageHeadingAccent}</span>
        </h1>
        <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px', marginBottom: '64px', maxWidth: '560px' }}>
          {ts.pageIntro}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {ts.items.map((item, idx) => {
            const alt = idx % 2 === 1;
            const accent = alt ? b.tidal : b.peach;
            return (
              <div key={item.initials} style={{
                alignSelf: narrow ? 'stretch' : (alt ? 'flex-end' : 'flex-start'),
                width: narrow ? '100%' : '80%',
                backgroundColor: alt ? b.white : b.firstLight, borderRadius: '20px',
                padding: narrow ? '32px 28px' : '40px 44px',
                border: `1px solid rgba(51,88,96,${alt ? '0.08' : '0.04'})`,
                boxShadow: alt ? '0 8px 30px rgba(51,88,96,0.06)' : 'none'
              }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: '58px', lineHeight: 0.9, color: accent, height: '30px' }} aria-hidden="true">&ldquo;</div>
                {item.full.map((para, i) => (
                  <p key={i} style={{ color: b.stone, fontSize: '15px', lineHeight: 1.85, margin: i === item.full.length - 1 ? 0 : '0 0 16px' }}>{para}</p>
                ))}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', marginTop: '24px' }}>
                  <p style={{ color: b.nearBlack, fontSize: '12px', letterSpacing: '0.16em', fontWeight: 600, margin: 0 }}>
                    <span style={{ color: accent }}>&mdash;</span>&nbsp;{item.initials}
                  </p>
                  <span aria-hidden="true" style={{ fontFamily: 'var(--serif)', fontSize: '48px', lineHeight: 0.5, color: accent, opacity: 0.4 }}>&rdquo;</span>
                </div>
              </div>
            );
          })}
        </div>

        <p style={{ color: 'rgba(78,106,112,0.5)', fontSize: '11px', lineHeight: 1.6, marginTop: '24px' }}>
          {ts.note}
        </p>

        <div style={{ marginTop: '64px', textAlign: 'center' }}>
          <button onClick={() => go('contact')} style={{
            padding: '16px 40px', borderRadius: '999px',
            backgroundColor: b.deepCove, color: b.white,
            fontSize: '14px', border: 'none', cursor: 'pointer'
          }}>
            {ts.backCta}
          </button>
        </div>
      </div>
    </section>
  );
}

function Home({ go }) {
  const narrow = useIsNarrow();
  const { t } = useLang();
  return (
    <>
      {/* Hero */}
      <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <img
          src={HERO_IMG}
          alt="Calm coastal beach"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: narrow ? '37% 60%' : 'center 60%', transform: 'scaleX(-1)'  }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(105deg, rgba(24,50,56,0.88) 0%, rgba(51,88,96,0.6) 36%, rgba(51,88,96,0.16) 62%, rgba(51,88,96,0) 100%)'
        }}/>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(20,40,44,0.3) 0%, transparent 26%)'
        }}/>

        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '120px 24px 80px', width: '100%', position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '510px' }}>
            <h1 style={{
              fontFamily: 'var(--serif)',
              color: b.white, fontSize: 'clamp(50px,7vw,78px)',
              lineHeight: 1.08, fontWeight: 300, letterSpacing: '0.01em', marginBottom: '24px'
            }}>
              {t.home.heroLines.map((ln, i) => <React.Fragment key={i}>{ln}<br /></React.Fragment>)}
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: 'rgba(247,243,226,0.9)' }}>{t.home.heroAccent}</span>
            </h1>
            <p style={{ color: b.white, fontSize: 'clamp(14px,1.5vw,16px)', fontWeight: 400, lineHeight: 1.6, letterSpacing: '0.01em', maxWidth: '480px', marginBottom: '14px' }}>
              {t.home.heroSubhead}
            </p>
            <p style={{ color: 'rgba(247,243,226,0.7)', fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500, lineHeight: 1.4, marginBottom: '40px' }}>
              {t.home.heroLangs}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <button onClick={() => go('contact')} style={{
                padding: '14px 32px', borderRadius: '999px',
                backgroundColor: b.white, color: b.deepCove,
                fontSize: '14px', fontWeight: 500, border: 'none', cursor: 'pointer'
              }}>
                {t.home.heroCta}
              </button>
            </div>
          </div>
        </div>
        {/* Gentle wavy bottom edge, revealing the white section below as a scroll cue */}
        <svg viewBox="0 0 1440 72" preserveAspectRatio="none" aria-hidden="true"
          style={{ position: 'absolute', left: 0, right: 0, bottom: -1, width: '100%', height: '72px', display: 'block', zIndex: 2 }}>
          <path fill="#FFFFFF" d="M0,36 C480,66 960,20 1440,10 L1440,72 L0,72 Z" />
        </svg>
      </section>

      {/* Pull quote */}
      <section style={{ backgroundColor: b.white, padding: '96px 0' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <p style={{
            fontFamily: 'var(--serif)', fontStyle: 'italic',
            color: b.nearBlack, fontSize: 'clamp(22px,3vw,30px)',
            fontWeight: 300, lineHeight: 1.5, letterSpacing: '0.01em', marginBottom: '28px'
          }}>
            {t.home.quoteLines.map((ln, i) => <React.Fragment key={i}>{ln}{i < t.home.quoteLines.length - 1 && <br />}</React.Fragment>)}
            <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.tidal }}>{t.home.quoteAccent}</span>
          </p>
          <p style={{ color: b.stone, fontSize: '16px', fontWeight: 400, lineHeight: 1.7, marginBottom: 0, maxWidth: '560px', marginLeft: 'auto', marginRight: 'auto', textWrap: 'balance' }}>
            {t.home.quoteBridge}
          </p>
        </div>
      </section>

      {/* Meet the counsellor */}
      <section style={{ backgroundColor: b.firstLight, padding: '96px 0' }}>
        <div style={{
          maxWidth: '1024px', margin: '0 auto', padding: '0 24px',
          display: 'grid', gridTemplateColumns: narrow ? '1fr' : '0.85fr 1fr', gap: narrow ? '36px' : '56px', alignItems: 'center'
        }}>
          {/* Portrait */}
          <div style={{ borderRadius: '20px', overflow: 'hidden', backgroundColor: '#E6EBDD', aspectRatio: '4/5', boxShadow: '0 12px 44px rgba(51,88,96,0.14)' }}>
            <img
              src={HOME_PORTRAIT}
              alt="Lindsey Sun, counsellor at The Cove"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 18%', display: 'block' }}
            />
          </div>

          {/* Intro */}
          <div>
            <p style={{ color: b.warm, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '24px' }}>
              {t.home.meetEyebrow}
            </p>
            <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '30px', fontWeight: 300, lineHeight: 1.2, marginBottom: '24px' }}>
              {t.home.meetHeadingPre}<span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.warm }}>{t.home.meetHeadingAccent}</span>
            </h2>
            {t.home.meetParas.map((p, i) => (
              <p key={i} style={{ color: b.stone, lineHeight: 1.8, fontSize: '16px', marginBottom: i === t.home.meetParas.length - 1 ? '32px' : '24px' }}>{p}</p>
            ))}
            <button onClick={() => go('about')} className="cta-link" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              color: b.warm, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0
            }}>
              <span style={{ textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationThickness: '1px' }}>{t.home.meetLink}</span>
              <ArrowRight size={13} className="cta-arrow" />
            </button>
          </div>
        </div>
      </section>

      {/* Who comes here */}
      <section style={{ backgroundColor: b.white, padding: '96px 0' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ maxWidth: '480px', marginBottom: '48px' }}>
            <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t.home.whoEyebrow}
            </p>
            <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '30px', fontWeight: 300, lineHeight: 1.3, marginBottom: '16px' }}>
              {t.home.whoHeading}
            </h2>
            <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px' }}>
              {withEmphasis(t.home.whoIntro)}
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3,1fr)', gap: '24px' }}>
            {t.home.whoCards.map(c => (
              <div key={c.label} style={{ padding: '32px', borderRadius: '16px', backgroundColor: b.firstLight, border: '1px solid rgba(51,88,96,0.06)', boxShadow: '0 6px 28px rgba(51,88,96,0.07)' }}>
                <div style={{ width: '32px', height: '1px', backgroundColor: b.warm, marginBottom: '24px' }} />
                <h3 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 300, lineHeight: 1.25, marginBottom: '12px' }}>{c.label}</h3>
                <p style={{ color: b.stone, fontSize: '14px', lineHeight: 1.75 }}>{withEmphasis(c.desc)}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '40px' }}>
            <button onClick={() => go('services')} className="cta-link" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              color: b.deepCove, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0
            }}>
              <span style={{ textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationThickness: '1px' }}>{t.home.servicesLink}</span>
              <ArrowRight size={13} className="cta-arrow" />
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ backgroundColor: b.sage, padding: '88px 0' }}>
        <div style={{ maxWidth: '820px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.24em', textTransform: 'uppercase', marginBottom: '28px' }}>
            {t.home.testimonials.eyebrow}
          </p>
          <TestimonialCarousel items={t.home.testimonials.items} labels={t.home.testimonials} go={go} />
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: b.firstLight, padding: '96px 0' }}>
        <div style={{ maxWidth: '640px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <p style={{ color: b.warm, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '24px' }}>
            {t.home.ctaEyebrow}
          </p>
          <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '30px', fontWeight: 300, lineHeight: 1.3, marginBottom: '24px' }}>
            {t.home.ctaHeading}
          </h2>
          <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px', marginBottom: '48px' }}>
            {t.home.ctaBody.map((ln, i) => <React.Fragment key={i}>{i > 0 && <br />}{ln}</React.Fragment>)}
          </p>
          <button onClick={() => go('contact')} style={{
            padding: '16px 40px', borderRadius: '999px',
            backgroundColor: b.deepCove, color: b.white,
            fontSize: '14px', border: 'none', cursor: 'pointer'
          }}>
            {t.common.bookCta}
          </button>
        </div>
      </section>
    </>
  );
}

// ── About ─────────────────────────────────────────────────────────────────────
function About({ go }) {
  const [imgErr, setImgErr] = useState(false);
  const narrow = useIsNarrow();

  return (
    <>
      {/* Philosophy — a concise note from the counsellor, over a coastal backdrop */}
      <section style={{ position: 'relative', backgroundColor: b.deepCove, overflow: 'hidden' }}>
        <img src={QUIET_IMG} alt="" aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', opacity: 0.95 }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(100deg, rgba(20,44,50,0.9) 0%, rgba(24,50,56,0.8) 55%, rgba(51,88,96,0.1) 100%)' }} />
        {/* Fade only the very bottom of the tea image to solid teal — enough to hide its
            edge and give the wave a solid base to meet, while revealing most of the image */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(51,88,96,0) 86%, #335860 100%)' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '1024px', margin: '0 auto', padding: '150px 24px 48px' }}>
          <div style={{ maxWidth: '680px' }}>
            <h1 style={{
              fontFamily: 'var(--serif)',
              color: b.firstLight, fontSize: 'clamp(40px,5vw,52px)',
              fontWeight: 300, lineHeight: 1.2, letterSpacing: '0.01em', marginBottom: '32px'
            }}>
              A quiet place to <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.peach }}>just be.</span>
            </h1>
            <p style={{ color: 'rgba(247,243,226,0.9)', lineHeight: 1.85, fontSize: '16px', marginBottom: '24px' }}>
              The Cove takes its name from a sheltered stretch of water, calm and protected from the open sea.
              That is the space I set out to create: unhurried, private, and free from judgment.
            </p>
            <p style={{ color: 'rgba(247,243,226,0.9)', lineHeight: 1.85, fontSize: '16px', margin: 0 }}>
              Here, your stories will be met with steadiness, genuine care, and all the time our work deserves.
              Whatever you are carrying, you will not have to carry it alone.
            </p>
            <p style={{ fontFamily: "'Dancing Script', cursive", color: b.peach, fontSize: '27px', fontWeight: 600, lineHeight: 1.2, margin: '24px 0 8px' }}>
              — Lindsey Sun
            </p>
            <p style={{ color: 'rgba(247,243,226,0.55)', fontSize: '12px', letterSpacing: '0.06em', margin: 0 }}>
              B.A., M.Soc.Sc., M.Couns.
            </p>
          </div>
        </div>
      </section>

      {/* Meet the counsellor — full-bleed portrait */}
      <section style={{ backgroundColor: b.white, position: 'relative' }}>
        {/* The band (now solid teal at its base) crests over the top of the portrait
            as a solid-teal wave — full-width top covers the seam, so no line, no photo edge. */}
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true"
          style={{ position: 'absolute', top: '-1px', left: 0, right: 0, width: '100%', height: '80px', display: 'block', zIndex: 3, pointerEvents: 'none' }}>
          <path fill={b.deepCove} d="M0,0 L1440,0 L1440,20 C1040,52 420,60 0,34 Z" />
        </svg>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', alignItems: 'stretch' }}>
          {/* Full-bleed portrait (bleeds to the left edge) */}
          <div style={{ backgroundColor: '#E6EBDD', minHeight: narrow ? '380px' : '620px' }}>
            <img
              src={ABOUT_PORTRAIT}
              alt="Lindsey Sun, counsellor at The Cove"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 18%', display: 'block' }}
            />
          </div>
          {/* Text */}
          <div style={{ display: 'flex', alignItems: 'center', padding: narrow ? '56px 24px' : '80px 6vw' }}>
            <div style={{ maxWidth: '520px' }}>
              <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '24px' }}>About me · Counsellor for cross-cultural professionals</p>
              <h2 style={{
                fontFamily: 'var(--serif)',
                color: b.nearBlack, fontSize: 'clamp(30px,4.5vw,40px)',
                fontWeight: 300, lineHeight: 1.2, letterSpacing: '0.01em', marginBottom: '24px'
              }}>
                Hi, I&apos;m <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.tidal }}>Lindsey.</span>
              </h2>
              <p style={{ color: b.stone, lineHeight: 1.75, fontSize: '16px', marginBottom: '16px' }}>
                I was <strong style={{ fontWeight: 600, color: b.nearBlack }}>born and raised in mainland China,
                educated in the United States</strong>, and <strong style={{ fontWeight: 600, color: b.nearBlack }}>I&apos;ve been calling Hong Kong home</strong> for more than a decade.
                Somewhere along the way I stopped trying to work out which set of rules I was supposed to live by,
                and started working out what I actually believed. It is probably why I don&apos;t think there is
                only one good way to live a life.
              </p>
              <p style={{ color: b.stone, lineHeight: 1.75, fontSize: '16px', marginBottom: '16px' }}>
                I have always been curious about why people are the way they are, and that curiosity first drew me
                to study psychology. In the years that followed, my work was in brand communications, and it was
                good work. But I kept noticing how cold a system can be even when the people inside it are kind, and
                how easily a person becomes a resource. I wanted to do work where people were simply people.
              </p>
              <p style={{ color: b.stone, lineHeight: 1.75, fontSize: '16px', marginBottom: '16px' }}>
                So I bring two things that do not often meet: <strong style={{ fontWeight: 600, color: b.nearBlack }}>a
                grounding in psychology, and years inside the fast-paced professional world many of my clients
                know</strong>, the kind where everything can look fine long after it has stopped feeling fine.
              </p>
              <p style={{ color: b.stone, lineHeight: 1.75, fontSize: '16px', marginBottom: '16px' }}>
                Now I sit with adults who are capable, high-achieving, and often quietly exhausted. We talk about
                work, love, family, and what you actually want from your life, in whichever of your languages gets
                closest to it.
              </p>
              <p style={{ color: b.stone, lineHeight: 1.75, fontSize: '16px', margin: 0 }}>
                My practice is openly <strong style={{ fontWeight: 600, color: b.nearBlack }}>progressive, sex-positive,
                and affirming</strong>. Whoever you are, however you love, and whatever your relationships look like,
                you are welcome here.
              </p>
              <PullQuote quote="What I value is Lindsey’s profound empathy." initials="A.Z." style={{ marginTop: '32px' }} />
            </div>
          </div>
        </div>
      </section>

      {/* What I believe — philosophy */}
      <section style={{ backgroundColor: b.deepCove, padding: '96px 0' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ maxWidth: '1024px' }}>
            <h2 style={{ fontFamily: 'var(--serif)', color: b.firstLight, fontSize: 'clamp(30px,4.5vw,40px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '44px' }}>What I believe.</h2>
            <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(2,1fr)', gap: narrow ? '36px' : '44px 64px' }}>
              {[
                { claim: 'Change asks for courage, not perfection.', c: b.peach, line: 'To me, change is about courage more than getting it right, and it rarely arrives all at once. A small, honest step is usually enough to begin, and simply choosing to sit in a counselling room takes real courage and strength, something I never take lightly.' },
                { claim: 'There is no single right way to live.', c: b.peach, line: 'I have come to believe there is no single right way to live, which means there is room for your life exactly as it is. Whoever you are, and however your life is shaped, I will meet you with acceptance, and never judgment.' },
                { claim: 'I don’t hold your answers. You do.', c: b.peach, line: 'The truth about your life is not mine to hold. It is yours. Every person is different, so I stay genuinely curious rather than assume I understand. What I trust is that you already carry a quiet wisdom about your own life, and my part is to help you hear it.' },
                { claim: 'Meaning is ours to make.', c: b.peach, line: 'I believe life is open-ended, and that meaning is something we make rather than find. So whatever you bring, big or small, I take it seriously. When you feel ready, I would love to hear from you.' },
              ].map(bel => (
                <div key={bel.claim}>
                  <h3 style={{ fontFamily: 'var(--serif)', color: bel.c, fontSize: 'clamp(21px,2.6vw,24px)', fontWeight: 400, lineHeight: 1.3, marginBottom: '12px' }}>{bel.claim}</h3>
                  <p style={{ color: 'rgba(247,243,226,0.82)', lineHeight: 1.75, fontSize: '15px', margin: 0 }}>{bel.line}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How I work — modalities */}
      <section style={{ backgroundColor: b.firstLight, padding: '96px 0' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ maxWidth: '640px', marginBottom: '44px' }}>
            <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(30px,4.5vw,40px)', fontWeight: 300, lineHeight: 1.2, margin: 0 }}>
              How I work.
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(2,1fr)', gap: narrow ? '36px' : '40px 64px', marginBottom: '56px' }}>
            {[
              { t: 'Where we begin', hc: b.deepCove, d: 'I mostly listen, and take the time to understand your story as you have lived it. I want it to feel safe enough to say the real thing, and however unconventional your story is, I will not judge it.' },
              { t: 'As we go deeper', hc: b.deepCove, d: 'I become more active, offering a perspective, naming a contradiction I notice, or inviting you into a different way of feeling or experiencing something. Never as the last word on your life, only to help you see what you might otherwise miss.' },
            ].map(ph => (
              <div key={ph.t}>
                <h3 style={{ fontFamily: 'var(--serif)', color: ph.hc, fontSize: '22px', fontWeight: 400, lineHeight: 1.3, marginBottom: '10px' }}>{ph.t}</h3>
                <p style={{ color: b.stone, lineHeight: 1.75, fontSize: '15px', margin: 0 }}>{ph.d}</p>
              </div>
            ))}
          </div>
          <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '20px' }}>Approaches I draw on</p>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3,1fr)', gap: '16px' }}>
            {[
              { t: 'Humanistic & Existential', d: 'The foundation of my work: the larger questions of meaning, identity, and freedom, and who you are beneath the roles you perform.' },
              { t: 'Emotion-Focused Therapy (EFT)', d: 'Turning toward emotions as information, and working through them rather than around them.' },
              { t: 'Narrative Therapy', d: 'Re-examining the stories told about your life, and finding room to author new ones.' },
              { t: 'Acceptance & Commitment Therapy (ACT)', d: 'Making room for difficult feelings while moving toward what genuinely matters to you.' },
              { t: 'Cognitive Behavioural Therapy (CBT)', d: 'Noticing the links between thoughts, feelings, and behaviour, and gently loosening the patterns that keep you stuck.' },
            ].map(m => (
              <div key={m.t} style={{ padding: '28px', borderRadius: '16px', backgroundColor: b.white, border: '1px solid rgba(51,88,96,0.06)', boxShadow: '0 6px 28px rgba(51,88,96,0.07)' }}>
                <div style={{ width: '28px', height: '1px', backgroundColor: b.peach, marginBottom: '16px' }} />
                <h3 style={{ color: b.nearBlack, fontSize: '16px', fontWeight: 500, lineHeight: 1.4, marginBottom: '8px' }}>{m.t}</h3>
                <p style={{ color: b.stone, fontSize: '14px', lineHeight: 1.7, margin: 0 }}>{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education & credentials */}
      <section style={{ backgroundColor: b.white, padding: '96px 0' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(30px,4.5vw,40px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '48px' }}>
            My credentials
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: narrow ? '48px' : '64px', alignItems: 'start' }}>
            {/* Credentials */}
            <div>
              <h3 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 400, lineHeight: 1.3, marginBottom: '24px' }}>
                Education &amp; training
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', margin: '0 0 -8px' }}>Formal education</p>
                {[
                  { degree: 'Master of Counselling', org: 'Monash University' },
                  { degree: 'Master of Social Science in Media Management', org: 'Hong Kong Baptist University' },
                  { degree: 'Bachelor of Arts in Psychology', org: 'Pennsylvania State University' },
                  { degree: 'Bachelor of Arts in Advertising & Public Relations', org: 'Pennsylvania State University' },
                ].map(e => (
                  <div key={e.degree} style={{ paddingLeft: '18px', borderLeft: `2px solid ${b.tidal}` }}>
                    <p style={{ color: b.nearBlack, fontSize: '15px', fontWeight: 500, lineHeight: 1.4 }}>{e.degree}</p>
                    <p style={{ color: b.stone, fontSize: '13px', marginTop: '3px' }}>{e.org}</p>
                  </div>
                ))}
                <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', margin: '16px 0 -8px' }}>Professional training</p>
                {[
                  { degree: 'Narrative Therapy, Level One', org: 'Dulwich Centre' },
                ].map(e => (
                  <div key={e.degree} style={{ paddingLeft: '18px', borderLeft: `2px solid ${b.peach}` }}>
                    <p style={{ color: b.nearBlack, fontSize: '15px', fontWeight: 500, lineHeight: 1.4 }}>{e.degree}</p>
                    <p style={{ color: b.stone, fontSize: '13px', marginTop: '3px' }}>{e.org}</p>
                  </div>
                ))}
              </div>
              <h3 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 400, lineHeight: 1.3, margin: '40px 0 24px' }}>
                Membership of Professional Associations
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {[
                  { name: 'Hong Kong Professional Counselling Association', note: 'Member no. M3910' },
                  { name: 'Greater Bay Psychology and Counselling Association', note: 'Member no. 74' },
                ].map(({ name, note }) => (
                  <div key={name} style={{ paddingLeft: '18px', borderLeft: `2px solid ${b.tidal}` }}>
                    <p style={{ color: b.nearBlack, fontSize: '15px', fontWeight: 500, lineHeight: 1.4 }}>{name}</p>
                    {note && <p style={{ color: b.stone, fontSize: '13px', marginTop: '3px' }}>{note}</p>}
                  </div>
                ))}
              </div>
            </div>
            {/* Portrait — framed with an offset accent block */}
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '20px', left: '20px', width: '100%', height: '100%', borderRadius: '16px', backgroundColor: b.sage, zIndex: 0 }} />
              <div style={{ position: 'relative', zIndex: 1, borderRadius: '16px', overflow: 'hidden', backgroundColor: '#E6EBDD', aspectRatio: '3/4', minHeight: '400px', boxShadow: '0 10px 34px rgba(51,88,96,0.12)' }}>
                {!imgErr ? (
                  <img
                    src={PORTRAIT_IMG}
                    alt="Lindsey Sun"
                    onError={() => setImgErr(true)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                  />
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ color: b.stone, fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                      [ Portrait ]
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Is this a fit? + first step */}
      <section style={{ backgroundColor: b.sage, padding: '96px 0' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(30px,4.5vw,40px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '16px' }}>
            Are we a good fit?
          </h2>
          <p style={{ color: b.stone, lineHeight: 1.75, fontSize: '16px', marginBottom: '40px', maxWidth: '620px' }}>
            More than any method, what helps most is the relationship between us. So it is worth finding out, early and without pressure, whether we are right for each other.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '16px', alignItems: 'start' }}>
            <div style={{ padding: narrow ? '28px' : '32px', borderRadius: '16px', backgroundColor: b.white, border: '1px solid rgba(51,88,96,0.06)', boxShadow: '0 6px 28px rgba(51,88,96,0.07)' }}>
              <h3 style={{ fontFamily: 'var(--serif)', color: b.deepCove, fontSize: '21px', fontWeight: 400, lineHeight: 1.3, marginBottom: '16px' }}>This is likely a fit</h3>
              {[
                'You want a real conversation, not a set of techniques.',
                'You think in ideas as well as feelings.',
                'You are building a life between cultures or languages. We can work in English, Mandarin, or Cantonese, and you can switch whenever you like.',
                'You want a space that is affirming and sex positive, at ease with LGBTQIA+ clients and with open and non-monogamous relationships.',
              ].map((item, i, arr) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '11px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(51,88,96,0.08)' : 'none', color: b.stone, fontSize: '15px', lineHeight: 1.6 }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: b.tidal, flexShrink: 0, marginTop: '7px' }} />
                  {item}
                </div>
              ))}
            </div>
            <div style={{ padding: narrow ? '28px' : '32px', borderRadius: '16px', backgroundColor: b.white, border: '1px solid rgba(51,88,96,0.06)', boxShadow: '0 6px 28px rgba(51,88,96,0.07)' }}>
              <h3 style={{ fontFamily: 'var(--serif)', color: b.warm, fontSize: '21px', fontWeight: 400, lineHeight: 1.3, marginBottom: '16px' }}>Perhaps not the right fit</h3>
              {[
                'The client would be a young child. I work with adults.',
                'You are hoping for quick advice or a guaranteed outcome. Counselling tends to work more slowly, through reflection and a willingness to look at what is underneath.',
                'What you need sits outside my scope. If so, I will gladly point you toward the right person.',
              ].map((item, i, arr) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '11px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(51,88,96,0.08)' : 'none', color: b.stone, fontSize: '15px', lineHeight: 1.6 }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: b.warm, flexShrink: 0, marginTop: '7px' }} />
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: narrow ? '56px' : '72px' }}>
            <h3 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(22px,2.6vw,26px)', fontWeight: 300, lineHeight: 1.35, marginBottom: '28px', maxWidth: '620px', marginLeft: 'auto', marginRight: 'auto' }}>
              If that sounds like the kind of space you have been looking for, I would love to hear from you.
            </h3>
            <button onClick={() => go('contact')} style={{
              padding: '16px 40px', borderRadius: '999px',
              backgroundColor: b.deepCove, color: b.white,
              fontSize: '14px', border: 'none', cursor: 'pointer'
            }}>
              Take the first step
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

// ── Services ──────────────────────────────────────────────────────────────────
function Services({ go }) {
  const narrow = useIsNarrow();
  return (
    <>
      {/* Does any of this sound familiar — opens the page */}
      <section style={{ backgroundColor: b.white, paddingTop: '128px', paddingBottom: '96px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <h1 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(40px,5vw,52px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '48px' }}>
            Does any of this sound{' '}<span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.tidal }}>familiar?</span>
          </h1>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: narrow ? '16px' : '20px 24px' }}>
            {[
              'You are the capable, dependable one, and more tired than you let anyone see.',
              'You keep meeting the same patterns with the people closest to you.',
              'The way you live or love doesn’t fit the usual script, and you carry quiet shame about it, or simply aren’t sure how to navigate it.',
              'You move between languages and cultures, unsure which version of you is the real one.',
            ].map((line, i) => (
              <div key={i} style={{ backgroundColor: b.firstLight, borderRadius: '16px', padding: narrow ? '26px' : '30px 32px', border: '1px solid rgba(51,88,96,0.08)', boxShadow: '0 6px 28px rgba(51,88,96,0.06)' }}>
                <div style={{ width: '28px', height: '2px', backgroundColor: b.warm, marginBottom: '18px' }} />
                <p style={{ fontFamily: 'var(--sans)', color: b.stone, fontSize: '16px', lineHeight: 1.65 }}>{line}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: '48px', fontSize: '16px', color: b.stone, lineHeight: 1.7 }}>
            The people I work with arrive with all kinds of things, and often more than one at once. If any of this sounds like you, you do not need to have it all together, or even the right words. You only have to begin.
          </p>
        </div>
      </section>

      {/* What working together looks like */}
      <section style={{ backgroundColor: b.firstLight, padding: '96px 0' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: narrow ? '40px' : '48px', alignItems: 'start' }}>
            <div>
              <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 300, lineHeight: 1.3, marginBottom: '24px' }}>
                What happens in a session
              </h2>
              <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px', marginBottom: '24px' }}>
                Sessions are a conversation, not a questionnaire. There is no agenda you have to follow. I tailor
                the work to you, and we go where it needs to go.
              </p>
              <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px' }}>
                Some people come with a specific concern. Others arrive with something less defined: a feeling of
                being stuck, a general heaviness, a sense that something is not quite right. Both are entirely valid
                places to begin.
              </p>
              <PullQuote quote="The &lsquo;big problems&rsquo; I was carrying could be broken down into smaller, more manageable parts." initials="R.K." style={{ marginTop: '32px', maxWidth: 'none' }} />
            </div>
            <div>
              <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 300, lineHeight: 1.3, marginBottom: '24px' }}>
                Some of what people bring
              </h2>
              {[
                'Work stress, pressure, and the cost of high performance',
                'Relationships, with partners and with family',
                'Depression and low mood',
                'Anxiety and persistent overwhelm',
                'Questions of meaning, direction, and identity',
                'Life transitions, grief, and major decisions',
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', fontSize: '14px', padding: '12px 0', borderBottom: '1px solid rgba(51,88,96,0.08)', color: b.stone, lineHeight: 1.6 }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: b.warm, flexShrink: 0, marginTop: '6px' }} />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* GSRD-affirming — a specific, explicit welcome, after the universal content */}
          <div style={{ marginTop: narrow ? '48px' : '64px', borderRadius: '22px', padding: narrow ? '30px 26px' : '44px 48px', backgroundColor: b.sage, boxShadow: '0 10px 34px rgba(51,88,96,0.10)' }}>
            <p style={{ fontFamily: 'var(--serif)', color: b.deepCove, fontSize: narrow ? '24px' : '29px', fontWeight: 300, lineHeight: 1.35, margin: '0 0 16px' }}>
              Whoever you are, and however you love, you are welcome here.
            </p>
            <p style={{ color: b.stone, lineHeight: 1.8, fontSize: '16px', margin: 0 }}>
              The Cove is a <strong style={{ fontWeight: 600, color: b.deepCove }}>sex-positive practice, affirming of gender, sexual and relationship diversity (GSRD)</strong>. All genders, sexualities and relationships are welcome here, including non-monogamous and open ones.
            </p>
          </div>
          <div style={{ textAlign: 'center', marginTop: narrow ? '56px' : '72px' }}>
            <button onClick={() => go('contact')} style={{ padding: '16px 40px', borderRadius: '999px', backgroundColor: b.deepCove, color: b.white, fontSize: '14px', border: 'none', cursor: 'pointer' }}>
              Take the first step
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function Process({ go }) {
  const narrow = useIsNarrow();
  const accent = '#1F7A83';
  const feeRows = [
    { title: 'Individual counselling', price: 'HKD 1,150', meta: '50 minutes',
      body: 'Ongoing one-to-one counselling, at a pace that suits you. Fifty minutes gives us room to settle, go deeper, and close gently. In person in Central or online, in English, Mandarin, and Cantonese.',
      cta: { label: 'Enquire to begin', href: SESSION, event: 'email_enquiry_click' } },
    { title: 'A free first conversation', price: 'No charge', meta: '15 minutes, online',
      body: 'A gentle, no-cost place to begin, with no commitment and nothing to prepare. More than any method, therapy works through the relationship between us, so this first step is about one thing, finding out together whether we are the right fit.',
      cta: { label: 'Book a free chat', href: CONSULT, event: 'book_consultation_click', external: true } },
    { title: 'Sliding-scale places', price: 'By arrangement', meta: 'Limited availability',
      body: 'I keep a limited number of sliding-scale places, where the fee is adjusted to what you can manage, for when the standard rate would be a barrier to beginning. If that is you, please reach out, and we can agree a rate that works.',
      cta: { label: 'Ask about a place', href: SLIDING, event: 'email_sliding_click' } },
  ];
  return (
    <>
      {/* Fees — lead */}
      <section style={{ backgroundColor: b.white, paddingTop: '128px', paddingBottom: '96px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <h1 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(40px,5vw,52px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '24px' }}>
            Sessions &amp; fees.
          </h1>
          <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px', marginBottom: '48px', maxWidth: '620px' }}>
            Money can be an awkward thing to bring up, so I would rather be plain about it. Here is what sessions cost, and the ways we can begin.
          </p>

          {/* The fee — an elevated menu panel */}
          <div style={{ borderRadius: '22px', backgroundColor: b.firstLight, border: '1px solid rgba(51,88,96,0.10)', overflow: 'hidden', boxShadow: '0 18px 50px rgba(51,88,96,0.09)' }}>
            <div style={{ height: '5px', background: `linear-gradient(90deg, ${b.tidal} 0%, ${accent} 55%, ${b.deepCove} 100%)` }} />
            <div style={{ padding: narrow ? '4px 24px 20px' : '8px 44px 26px' }}>
              {feeRows.map((row, i) => (
                <div key={i} style={{ padding: '30px 0', borderTop: i === 0 ? 'none' : '1px solid rgba(51,88,96,0.13)' }}>
                  <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', justifyContent: 'space-between', alignItems: narrow ? 'flex-start' : 'flex-end', gap: narrow ? '16px' : '40px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontFamily: 'var(--serif)', fontSize: narrow ? '23px' : '26px', fontWeight: 400, color: b.nearBlack, lineHeight: 1.2 }}>{row.title}</h3>
                      <p style={{ fontSize: '13px', color: b.stone, marginTop: '7px', letterSpacing: '0.02em' }}>{row.meta}</p>
                      <p style={{ fontSize: '15px', color: b.stone, lineHeight: 1.8, maxWidth: '540px', marginTop: '14px' }}>{withEmphasis(row.body)}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: narrow ? 'flex-start' : 'flex-end', gap: '10px', flexShrink: 0 }}>
                      <span style={{ fontFamily: 'var(--serif)', fontSize: '22px', fontWeight: 500, color: accent, whiteSpace: 'nowrap' }}>{row.price}</span>
                      <a href={row.cta.href} {...(row.cta.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} onClick={() => track(row.cta.event)} className="cta-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: accent, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                        <span style={{ textDecoration: 'underline', textUnderlineOffset: '4px', textDecorationThickness: '1px' }}>{row.cta.label}</span>
                        <ArrowRight size={13} className="cta-arrow" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to expect — step cards */}
      <section style={{ backgroundColor: b.sage, padding: '96px 0' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(30px,4.5vw,40px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '16px' }}>
            What to expect
          </h2>
          <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px', marginBottom: '48px', maxWidth: '560px' }}>
            There is nothing to prepare, and no right way to start. Here is how the work usually unfolds, from your first message onward.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: narrow ? '16px' : '20px 24px' }}>
          {[
            { n: '01', title: 'Before we begin',        body: 'When you reach out, I’ll reply personally, usually within a day, and we’ll find a first time that suits you. Ahead of it I send a short **consent form** covering confidentiality and how we work, so none of the process is a surprise. You are welcome to ask me anything before we start.' },
            { n: '02', title: 'Our first session',       body: 'The first session is a relaxed, unhurried conversation. We talk about what has brought you here, a little of your history if you wish, and what you are hoping for. I’ll also share how I work, so by the end you have a real sense of whether we fit. **There is no pressure to tell me everything at once.**' },
            { n: '03', title: 'Finding our rhythm',      body: 'Most people meet **weekly or fortnightly**, in sessions of fifty minutes, though we shape this around your life. Early on, my work is mostly to listen closely and make it safe enough to say the real thing. As trust grows I become more active, offering a perspective, gently naming a pattern, and inviting you to see something from a new angle.' },
            { n: '04', title: 'Reviewing and moving on', body: 'We check in openly on how the work is going, and change course whenever that helps. Therapy does not go on indefinitely. When you feel ready, we bring things to a considered close, so you leave with **tools, insight, and a steadier sense of yourself** to carry forward.' },
          ].map((s, i) => (
            <div key={s.n} style={{ backgroundColor: b.white, borderRadius: '16px', padding: narrow ? '28px' : '32px 34px', border: '1px solid rgba(51,88,96,0.06)', boxShadow: '0 6px 28px rgba(51,88,96,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                <div style={{ width: '46px', height: '46px', borderRadius: '50%', backgroundColor: b.deepCove, color: b.firstLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--serif)', fontSize: '19px', fontWeight: 400, flexShrink: 0, boxShadow: '0 6px 18px rgba(51,88,96,0.20)' }}>
                  {s.n}
                </div>
                <h3 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 400 }}>{s.title}</h3>
              </div>
              <p style={{ color: b.stone, fontSize: '15px', lineHeight: 1.8 }}>{withEmphasis(s.body)}</p>
            </div>
          ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: narrow ? '56px' : '72px' }}>
            <button onClick={() => go('contact')} style={{ padding: '16px 40px', borderRadius: '999px', backgroundColor: b.deepCove, color: b.white, fontSize: '14px', border: 'none', cursor: 'pointer' }}>
              Take the first step
            </button>
          </div>
        </div>
      </section>

    </>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);

  const items = [
    { q: 'Do I need to have a specific problem to start therapy?',
      a: 'Not at all. Many people come simply because something feels off, without being able to name it clearly. A general sense of heaviness, of being stuck, or of not quite being yourself is entirely enough to begin.' },
    { q: 'Is The Cove LGBTQ+ affirming?',
      a: 'Yes, wholeheartedly. This is a **sex-positive, affirming space** for people of all genders and sexualities, and for every kind of relationship, including non-monogamous, open, and other consensual structures. Who you are and how you love are respected here, and are never treated as something to question or fix.' },
    { q: 'What actually happens in a session?',
      a: "Sessions are a conversation. There is no agenda to follow and no worksheet to complete. We talk about what's going on for you, and the conversation goes where it needs to go." },
    { q: 'What is the free consultation for?',
      a: 'It is a 15-minute online conversation, **no cost and no commitment**. A chance to share a little about what has brought you here, to ask anything you want to know, and for both of us to find out whether this feels like the right fit.' },
    { q: 'How many sessions will I need?',
      a: 'There is no fixed answer. Some people find real clarity in a small number of sessions. Others choose to work over a longer period. We check in on this regularly and the pace is always in your hands.' },
    { q: 'How much does a session cost?',
      a: 'Sessions are 50 minutes, **HKD 1,150** each. I also keep a limited number of **reduced-fee (sliding-scale) places** for when cost is a barrier to beginning, so if that is you, please just ask.' },
    { q: 'Is everything confidential?',
      a: 'Yes. Everything shared in sessions is held in **strict confidence**, in line with professional ethical standards. The only exceptions are rare situations involving a serious risk of harm.' },
    { q: 'Do you offer online counselling in Hong Kong?',
      a: 'Yes. **Online sessions are available** and work well for many clients, whether you have a full schedule, live outside Hong Kong, or simply prefer the familiarity of your own space.' },
    { q: 'Do you offer in-person sessions, and where?',
      a: 'Yes. In-person sessions are held at **AMindset Psychology Services** in Central, at Suites 2116 & 2317, LKF Tower, 33 Wyndham Street, where I practise as part of their team of qualified counsellors. Online sessions are available too, wherever you are.' },
    { q: 'What languages do you work in?',
      a: 'Sessions are available in **English, Mandarin (普通话), and Cantonese (廣東話)**. You are welcome to move between languages within a session if that serves you better.' },
    { q: 'What is the cancellation policy?',
      a: 'Please give at least 24 hours\' notice if you need to cancel or reschedule. Sessions cancelled with less than 24 hours\' notice may be charged in full.' },
  ];

  return (
    <section style={{ backgroundColor: b.white, paddingTop: '128px', paddingBottom: '96px' }}>
      <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
        <h1 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(40px,5vw,52px)', fontWeight: 300, marginBottom: '24px' }}>
          In case you&apos;re{' '}<span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.tidal }}>wondering.</span>
        </h1>
        <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px', marginBottom: '48px', maxWidth: '560px' }}>
          A few things people often wonder about before reaching out. If your question isn&apos;t here, please ask. There&apos;s no such thing as a silly one, and I&apos;d be glad to answer it.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {items.map((item, i) => (
            <div key={i} style={{ borderRadius: '16px', overflow: 'hidden', backgroundColor: open === i ? 'rgba(108,177,184,0.10)' : b.white, border: '1px solid rgba(51,88,96,0.06)', borderLeft: open === i ? `3px solid ${b.tidal}` : '1px solid rgba(51,88,96,0.06)', boxShadow: '0 6px 28px rgba(51,88,96,0.07)' }}>
              <button
                style={{ width: '100%', textAlign: 'left', padding: '20px 28px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px', background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => setOpen(open === i ? null : i)}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: open === i ? b.tidal : b.nearBlack, lineHeight: 1.5 }}>{item.q}</span>
                {open === i
                  ? <ChevronUp   size={16} style={{ flexShrink: 0, marginTop: '2px', color: b.tidal  }} />
                  : <ChevronDown size={16} style={{ flexShrink: 0, marginTop: '2px', color: b.tidal  }} />}
              </button>
              {open === i && (
                <div style={{ padding: '0 28px 24px' }}>
                  <p style={{ fontSize: '14px', color: b.stone, lineHeight: 1.8 }}>{withEmphasis(item.a)}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function Contact() {
  // ── Web3Forms integration ─────────────────────────────────────────────
  // Paste your Web3Forms access key below. It's free and needs no account —
  // go to https://web3forms.com, enter the inbox that should receive enquiries,
  // and copy the key they email you into the quotes.
  const WEB3FORMS_ACCESS_KEY = '0e471179-61e9-48be-89b0-8f71d39b354e';
  const [status, setStatus] = useState('idle'); // idle | submitting | error
  const [showBooker, setShowBooker] = useState(false);
  const narrow = useIsNarrow();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    const formData = new FormData(e.target);
    try {
      const res = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        track('generate_lead', { method: 'contact_form' });
        navigate('/thank-you');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section style={{ backgroundColor: b.white, paddingTop: '128px', paddingBottom: '96px' }}>
      <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ maxWidth: '480px', marginBottom: '48px' }}>
          <h1 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(40px,5vw,52px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '24px' }}>
            Say{' '}<span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.tidal }}>hello.</span>
          </h1>
          <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px' }}>
            If any of this sounds familiar, the burnout, the in-between feeling, the sense of holding too much for too long, I&apos;d like to hear from you. There&apos;s no pressure, and no wrong way to reach out.
          </p>
        </div>

        {/* Primary booking panel — heading + a client's words, then the two options */}
        <div style={{ borderRadius: '20px', padding: narrow ? '28px' : '40px', backgroundColor: b.sage, marginBottom: '24px', boxShadow: '0 10px 34px rgba(51,88,96,0.10)' }}>
          <div style={{ marginBottom: '28px' }}>
            <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '16px' }}>Book a first appointment</p>
            <p style={{ fontFamily: 'var(--serif)', color: b.deepCove, fontSize: 'clamp(24px,3vw,30px)', fontWeight: 300, lineHeight: 1.3, margin: 0 }}>
              Two ways to start
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '16px' }}>
            {/* Option 1 — free conversation */}
            <div style={{ display: 'flex', flexDirection: 'column', borderRadius: '16px', padding: '24px', backgroundColor: b.white }}>
              <p style={{ fontFamily: 'var(--serif)', color: b.deepCove, fontSize: '19px', fontWeight: 500, marginBottom: '8px' }}>
                A free first conversation
              </p>
              <p style={{ color: b.stone, fontSize: '14px', lineHeight: 1.7, marginBottom: '20px', flexGrow: 1 }}>
                A relaxed 15-minute chat online, just to meet and see how it feels to talk together. No pressure, no cost, and no assumption I&apos;m the right person for you.
              </p>
              <button onClick={() => { track('book_consultation_click'); setShowBooker(true); setTimeout(() => { const el = document.getElementById('book-chat'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 80); }} style={{ display: 'block', width: '100%', textAlign: 'center', padding: '14px 20px', borderRadius: '999px', backgroundColor: 'transparent', border: `1px solid ${b.deepCove}`, color: b.deepCove, fontSize: '15px', fontWeight: 500, cursor: 'pointer' }}>
                Book a free chat
              </button>
            </div>
            {/* Option 2 — first session */}
            <div style={{ display: 'flex', flexDirection: 'column', borderRadius: '16px', padding: '24px', backgroundColor: b.white }}>
              <p style={{ fontFamily: 'var(--serif)', color: b.deepCove, fontSize: '19px', fontWeight: 500, marginBottom: '8px' }}>
                Begin therapy
              </p>
              <p style={{ color: b.stone, fontSize: '14px', lineHeight: 1.7, marginBottom: '20px', flexGrow: 1 }}>
                Ready to begin? Reach out and I&apos;ll walk you through what it involves, the fees, availability, and the consent form, and we&apos;ll find a first time that fits.{' '}
                <Link to="/process" style={{ color: b.deepCove, fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                  See what to expect here.
                </Link>
              </p>
              <a href={SESSION} onClick={() => track('email_enquiry_click')} style={{ display: 'block', textAlign: 'center', padding: '14px 20px', borderRadius: '999px', backgroundColor: b.deepCove, color: b.white, border: '1px solid transparent', fontSize: '15px', fontWeight: 500, textDecoration: 'none' }}>
                Ask about getting started
              </a>
            </div>
          </div>
        </div>

        {/* 15-minute booker — Google appointment schedule, revealed on request */}
        {showBooker && (
        <div id="book-chat" style={{ borderRadius: '20px', padding: narrow ? '20px' : '28px', backgroundColor: b.white, border: '1px solid rgba(51,88,96,0.10)', marginBottom: '24px', boxShadow: '0 10px 34px rgba(51,88,96,0.08)', scrollMarginTop: '96px' }}>
          <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '8px' }}>Free 15-minute chat</p>
          <p style={{ fontFamily: 'var(--serif)', color: b.deepCove, fontSize: 'clamp(22px,2.6vw,26px)', fontWeight: 300, lineHeight: 1.3, margin: '0 0 20px' }}>
            Pick a time that suits you
          </p>
          <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(51,88,96,0.08)' }}>
            <iframe
              src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ0I_paFAWr0jg2Ksp8hRnaxhtF0c4K912RtD-MxQ5rUoIUW6FUMB07xdeCgBi7U9MKNq5J_hc23?gv=true"
              title="Book a free 15-minute chat with Lindsey"
              style={{ border: 0, display: 'block', width: '100%', height: narrow ? '680px' : '600px' }}
              frameBorder="0"
            ></iframe>
          </div>
        </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 380px', gap: '24px', alignItems: 'start' }}>
          {/* Form */}
          <div style={{ order: narrow ? 2 : 0, borderRadius: '20px', padding: '32px', backgroundColor: b.firstLight, border: '1px solid rgba(51,88,96,0.08)', boxShadow: '0 6px 28px rgba(51,88,96,0.07)' }}>
            <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 300, marginBottom: '24px' }}>
              Prefer to write first?
            </h2>
            {status === 'success' ? (
              <div style={{ padding: '12px 0 20px' }}>
                <p style={{ fontFamily: 'var(--serif)', color: b.deepCove, fontSize: '22px', fontWeight: 300, lineHeight: 1.4, marginBottom: '12px' }}>
                  Thank you for reaching out.
                </p>
                <p style={{ color: b.stone, fontSize: '16px', lineHeight: 1.75 }}>
                  Your message has arrived safely. I read every note personally and will reply as soon as I can, usually within a day or two.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Web3Forms required + helper fields */}
                <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} />
                <input type="hidden" name="subject" value="New enquiry from The Cove Counselling website" />
                <input type="hidden" name="from_name" value="The Cove Counselling website" />
                {/* Honeypot — invisible to people, catches spam bots */}
                <input type="checkbox" name="botcheck" tabIndex={-1} aria-hidden="true" style={{ display: 'none' }} />

                <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {[
                    { label: 'Name',  name: 'name',  placeholder: 'Your name',        type: 'text'  },
                    { label: 'Email', name: 'email', placeholder: 'your@email.com',   type: 'email' },
                  ].map(f => (
                    <div key={f.label}>
                      <label style={{ display: 'block', fontSize: '14px', color: b.stone, marginBottom: '8px' }}>{f.label}</label>
                      <input type={f.type} name={f.name} placeholder={f.placeholder} required
                        style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', fontSize: '14px', border: '1px solid rgba(51,88,96,0.2)', backgroundColor: b.white, color: b.nearBlack, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: b.stone, marginBottom: '8px' }}>What brings you to The Cove?</label>
                  <select name="reason" style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', fontSize: '14px', border: '1px solid rgba(51,88,96,0.2)', backgroundColor: b.white, color: b.nearBlack, outline: 'none' }}>
                    <option value="">Whatever feels right</option>
                    <option>I&apos;m exploring and have a few questions</option>
                    <option>I&apos;d like to book a free first conversation</option>
                    <option>I&apos;d like to book a first session</option>
                    <option>Something else</option>
                  </select>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: b.stone, marginBottom: '8px' }}>Message</label>
                  <textarea name="message" rows={4} required placeholder="A few words about what's brought you here, however much or little feels right."
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', fontSize: '14px', border: '1px solid rgba(51,88,96,0.2)', backgroundColor: b.white, color: b.nearBlack, outline: 'none', resize: 'none', boxSizing: 'border-box' }} />
                </div>
                <button type="submit" disabled={status === 'submitting'}
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', fontSize: '14px', fontWeight: 500, backgroundColor: b.deepCove, color: b.white, border: 'none', cursor: status === 'submitting' ? 'default' : 'pointer', opacity: status === 'submitting' ? 0.7 : 1 }}>
                  {status === 'submitting' ? 'Sending…' : 'Send message'}
                </button>
                {status === 'error' && (
                  <p style={{ color: b.warm, fontSize: '14px', lineHeight: 1.6, marginTop: '14px' }}>
                    Something went wrong sending your message. Please try again, or email hello@thecovecounselling.com directly.
                  </p>
                )}
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ order: narrow ? 1 : 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <PullQuote quote="After the first time I talked with Lindsey, I was surprised by how well she understood my situation, and her words gave me lots of support and comfort." initials="A.Z." style={{ paddingLeft: '20px', maxWidth: 'none', marginBottom: '8px' }} />
            <div style={{ borderRadius: '20px', padding: '28px', backgroundColor: b.firstLight, border: '1px solid rgba(51,88,96,0.06)', boxShadow: '0 6px 28px rgba(51,88,96,0.07)' }}>
              <p style={{ color: b.warm, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '24px' }}>Details</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: b.stone }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Mail size={14} style={{ color: b.tidal, flexShrink: 0 }} />
                  <a href="mailto:hello@thecovecounselling.com" onClick={() => track('email_click')} style={{ color: b.stone, textDecoration: 'none' }}>
                    hello@thecovecounselling.com
                  </a>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <WhatsAppIcon size={14} color={b.tidal} />
                  <a href="https://wa.link/8sd6o4" target="_blank" rel="noopener noreferrer" onClick={() => track('whatsapp_click')} style={{ color: b.stone, textDecoration: 'none' }}>
                    +852 9065 1097
                  </a>
                </div>
                <WeChatContact iconColor={b.tidal} textColor={b.stone} iconSize={14} gap={12} />
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <MapPin size={14} style={{ color: b.tidal, flexShrink: 0, marginTop: '4px' }} />
                  <a href={AMINDSET_MAP} target="_blank" rel="noopener noreferrer" onClick={() => track('map_click')}
                    style={{ color: b.stone, textDecoration: 'none', lineHeight: 1.6 }}>
                    <span style={{ display: 'block', fontWeight: 500, color: b.nearBlack }}>AMindset Psychology Services</span>
                    Suites 2116 &amp; 2317, LKF Tower<br />
                    33 Wyndham Street, Central, Hong&nbsp;Kong
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Privacy ───────────────────────────────────────────────────────────────────
function Privacy() {
  const H = ({ children }) => (
    <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 300, lineHeight: 1.3, margin: '48px 0 18px' }}>{children}</h2>
  );
  const P = ({ children }) => (
    <p style={{ color: b.stone, lineHeight: 1.85, fontSize: '16px', marginBottom: '16px' }}>{children}</p>
  );
  const Item = ({ label, children }) => (
    <p style={{ color: b.stone, lineHeight: 1.85, fontSize: '16px', marginBottom: '12px' }}>
      <strong style={{ color: b.nearBlack, fontWeight: 500 }}>{label}: </strong>{children}
    </p>
  );

  return (
    <section style={{ backgroundColor: b.white, paddingTop: '128px', paddingBottom: '96px' }}>
      <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
        <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '24px' }}>Privacy</p>
        <h1 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(40px,5vw,52px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '24px' }}>
          Privacy Policy.
        </h1>
        <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px', marginBottom: '8px' }}>
          Please contact{' '}
          <a href="mailto:hello@thecovecounselling.com" style={{ color: b.deepCove, textDecoration: 'underline', textUnderlineOffset: '3px' }}>hello@thecovecounselling.com</a>
          {' '}for any questions.
        </p>

        <H>Confidentiality</H>
        <P>
          All information you share with The Cove Counselling is confidential and will not be released
          without your written authorization, except as required by law. All personal data collected is
          managed in compliance with the Personal Data (Privacy) Ordinance (Cap. 486) of Hong Kong. This
          data is gathered solely for purposes such as fee collection, communication, and arranging appointments.
        </P>
        <P>
          The Cove Counselling may use secure, privacy-compliant transcription software to transcribe and
          summarize session notes. While recordings are not stored, summarized notes may be retained as part
          of your confidential record. All notes are reviewed to ensure accuracy and to reduce any potential
          biases from the technology, which may also provide enhanced clinical insight and better outcomes in
          the care process.
        </P>

        <H>Exceptions to Confidentiality</H>
        <P>There are specific and limited exceptions to confidentiality, which include:</P>
        <Item label="Imminent Danger">
          If there is a risk of imminent danger to you or another person, The Cove Counselling is legally and
          ethically required to take steps to prevent harm, which may include contacting the authorities without
          your consent.
        </Item>
        <Item label="Suspected Abuse or Neglect">
          If there is reasonable suspicion of abuse, neglect, or risk thereof, The Cove Counselling must report
          this to the proper authorities, regardless of consent.
        </Item>
        <Item label="Legal Obligations">
          Any request for personal information to be shared with other professionals (e.g., physician, lawyer)
          will require your written permission unless mandated by a court order.
        </Item>
        <Item label="Transfer of Files">
          If you choose to share information with other professionals, this may involve the electronic or physical
          transfer of files, which carries a confidentiality risk. Additionally, transcription technology is used
          for session summaries, and while anonymized data may be shared for system improvements, confidentiality
          remains a priority.
        </Item>

        <H>Payment Policy</H>
        <Item label="Insurance">
          The Cove Counselling does not accept insurance for payment of services. We will help with any paperwork;
          however, sessions may not qualify for insurance reimbursement.
        </Item>
        <Item label="Payment Terms">
          Payments can be made via bank transfer, cash, or credit card, either in advance or within 24 hours of
          the session. Invoices and receipts will be sent via email. Please note that time spent in sessions,
          responding to emails, preparing documents, phone consultations, and any other related activities are
          billed equally.
        </Item>
        <Item label="Unpaid Sessions">
          If a session remains unpaid, no further services will be provided until all outstanding fees are settled.
          This policy is in place to ensure the continuity and effectiveness of the support provided.
        </Item>
        <Item label="Debt Collection">
          In the event of non-payment, The Cove Counselling reserves the right to take appropriate legal action to
          recover the debt. All debt collection practices will comply with relevant Hong Kong laws and will not
          compromise your confidentiality.
        </Item>

        <H>Cancellation Policy</H>
        <P>
          A minimum of 24 hours&apos; notice is required for a refund on a cancelled session. Sessions cancelled
          with less than 24 hours&apos; notice will be billed at the full rate. Exceptions may be made for
          unavoidable circumstances such as illness or accidents, but prompt notification is required.
        </P>

        <H>Disclaimer</H>
        <P>Our work together is a collaborative experience. Consultations alone are not therapy.</P>
        <P>
          We are committed to providing excellent service while ensuring the privacy of all individuals who share
          personal information with us, including data collected through our website.
        </P>
      </div>
    </section>
  );
}

// ── Emergency Resources ───────────────────────────────────────────────────────
function Emergency() {
  const hotlines = [
    { name: 'The Samaritans', number: '2896 0000', tel: '+85228960000',
      note: '24 hours · Confidential emotional support and suicide prevention, in English, Cantonese, Mandarin and more.' },
    { name: 'Suicide Prevention Services — Life Hotline', number: '2382 0000', tel: '+85223820000',
      note: '24 hours · Emotional support and suicide prevention for anyone in distress; separate youth line on 2382 0777.' },
    { name: 'The Samaritan Befrienders Hong Kong', number: '2389 2222', tel: '+85223892222',
      note: '24 hours · Confidential emotional support and suicide prevention; dedicated English line on 2389 2223.' },
    { name: 'OpenUp', number: 'openup.hk', href: 'https://www.openup.hk',
      note: '24 hours · Free online chat and text support for young people aged 11–35 (SMS 9101 2012), in English and Cantonese.' },
  ];

  const H = ({ children }) => (
    <h2 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: '22px', fontWeight: 300, lineHeight: 1.3, margin: '52px 0 20px' }}>{children}</h2>
  );

  return (
    <section style={{ backgroundColor: b.white, paddingTop: '128px', paddingBottom: '96px' }}>
      <div style={{ maxWidth: '1024px', margin: '0 auto', padding: '0 24px' }}>
        <p style={{ color: b.deepCove, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '24px' }}>Emergency</p>
        <h1 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(40px,5vw,52px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '24px' }}>
          If you need help now.
        </h1>
        <p style={{ color: b.stone, lineHeight: 1.7, fontSize: '16px', marginBottom: '48px' }}>
          The Cove Counselling is a private practice, not an emergency or crisis service. If you are in
          distress or worried about your safety, the free and confidential services below are available
          around the clock.
        </p>

        {/* Immediate danger */}
        <div style={{ borderRadius: '16px', padding: '24px 28px', backgroundColor: b.deepCove, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap' }}>
          <div>
            <p style={{ color: b.peach, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '8px' }}>If you are in immediate danger</p>
            <p style={{ color: b.firstLight, fontSize: '16px', lineHeight: 1.6, maxWidth: '360px' }}>
              Call emergency services now for police, fire, or ambulance, or go to your nearest hospital A&amp;E.
            </p>
          </div>
          <a href="tel:999" style={{ fontFamily: 'var(--serif)', color: b.white, fontSize: '48px', fontWeight: 300, textDecoration: 'none', lineHeight: 1 }}>999</a>
        </div>

        <H>24-hour crisis hotlines</H>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {hotlines.map(h => (
            <div key={h.name} style={{ borderRadius: '16px', padding: '22px 26px', backgroundColor: b.white, border: '1px solid rgba(51,88,96,0.07)', boxShadow: '0 6px 28px rgba(51,88,96,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <h3 style={{ color: b.nearBlack, fontSize: '16px', fontWeight: 500, lineHeight: 1.4 }}>{h.name}</h3>
                <a href={h.href || `tel:${h.tel}`} target={h.href ? '_blank' : undefined} rel={h.href ? 'noopener noreferrer' : undefined}
                  style={{ fontFamily: 'var(--serif)', color: b.deepCove, fontSize: '22px', fontWeight: 300, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  {h.number}
                </a>
              </div>
              <p style={{ color: b.stone, fontSize: '14px', lineHeight: 1.7, marginTop: '8px' }}>{h.note}</p>
            </div>
          ))}
        </div>
        <p style={{ color: b.stone, fontSize: '14px', lineHeight: 1.7, marginTop: '16px' }}>
          For a fuller directory of Hong Kong services, MIND HK maintains a{' '}
          <a href="https://www.mind.org.hk/find-help-now/" target="_blank" rel="noopener noreferrer" style={{ color: b.deepCove, textDecoration: 'underline', textUnderlineOffset: '3px' }}>Find Help Now</a> page.
        </p>
      </div>
    </section>
  );
}

// ── Floating WhatsApp button ────────────────────────────────────────────────────
// Fixed to the bottom-right — a low-pressure way to reach out. Hidden at the very
// top so it doesn't disrupt the hero, then fades in once you scroll down.
function WhatsAppFloat() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.55);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <a
      href="https://wa.link/8sd6o4"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with The Cove on WhatsApp"
      onClick={() => track('whatsapp_click')}
      className="wa-float"
      style={{
        position: 'fixed', bottom: '24px', right: '24px', zIndex: 60,
        width: '56px', height: '56px', borderRadius: '50%',
        // Muted, sage-leaning green — in the same natural family as b.sage/b.tidal,
        // but deep enough that the white glyph still reads (not WhatsApp's neon green).
        backgroundColor: '#5FA07A',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 6px 20px rgba(0,0,0,0.22)', textDecoration: 'none',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}>
      <WhatsAppIcon size={30} color="#FFFFFF" />
    </a>
  );
}

// ── Thank you (post-enquiry confirmation; the lead conversion page) ─────────────
function ThankYou() {
  return (
    <section style={{ backgroundColor: b.white, paddingTop: '160px', paddingBottom: '120px' }}>
      <meta name="robots" content="noindex" />
      <title>The Cove | Thank you</title>
      <div style={{ maxWidth: '620px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
        <p style={{ color: b.warm, fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '20px' }}>Message received</p>
        <h1 style={{ fontFamily: 'var(--serif)', color: b.nearBlack, fontSize: 'clamp(34px,5vw,48px)', fontWeight: 300, lineHeight: 1.2, marginBottom: '20px' }}>
          Thank you for <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 500, color: b.tidal }}>reaching out.</span>
        </h1>
        <p style={{ color: b.stone, fontSize: '16px', lineHeight: 1.75, marginBottom: '32px' }}>
          Your message has arrived safely. I read every note personally and will reply as soon as I can, usually within a day or two.
        </p>
        <Link to="/" style={{ display: 'inline-block', padding: '14px 32px', borderRadius: '999px', backgroundColor: b.deepCove, color: b.white, fontSize: '14px', fontWeight: 500, textDecoration: 'none' }}>
          Back to home
        </Link>
      </div>
    </section>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <LangContext.Provider value={{ t: T.en }}>
        <AppShell />
      </LangContext.Provider>
    </BrowserRouter>
  );
}

function AppShell() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const go = (id) => navigate(pathFor(id));
  const page = idForPath(location.pathname);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset scroll on route change (client navigation + back/forward)
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  return (
    <div style={{ backgroundColor: b.white, color: b.nearBlack }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&family=Dancing+Script:wght@400;600&display=swap');
        :root { --serif: 'Cormorant Garamond', serif; --sans: 'DM Sans', system-ui, sans-serif; }
        * { font-family: var(--sans); box-sizing: border-box; margin: 0; padding: 0; }
        html, body { overflow-x: hidden; }
        img { max-width: 100%; }
        p { text-wrap: pretty; }
        h1, h2, h3 { text-wrap: balance; }
        .wa-float { transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.6s ease; }
        .wa-float:hover { transform: scale(1.06); box-shadow: 0 10px 28px rgba(0,0,0,0.28); }
        .cta-arrow { transition: transform 0.2s ease; }
        .cta-link:hover .cta-arrow { transform: translateX(4px); }
        @keyframes tstFade { from { opacity: 0; } to { opacity: 1; } }
        .tst-fade { animation: tstFade 1.1s ease-in-out; }
      `}</style>
      <Navbar page={page} scrolled={scrolled} />
      <Routes>
        <Route path="/"             element={<><Seo id="home" /><Home go={go} /></>} />
        <Route path="/about"        element={<><Seo id="about" /><About go={go} /></>} />
        <Route path="/services"     element={<><Seo id="services" /><Services go={go} /></>} />
        <Route path="/process"      element={<><Seo id="process" /><Process go={go} /></>} />
        <Route path="/testimonials" element={<><Seo id="testimonials" /><Testimonials go={go} /></>} />
        <Route path="/faq"          element={<><Seo id="faq" /><FAQ /></>} />
        <Route path="/contact"      element={<><Seo id="contact" /><Contact /></>} />
        <Route path="/privacy"      element={<><Seo id="privacy" /><Privacy /></>} />
        <Route path="/emergency"    element={<><Seo id="emergency" /><Emergency /></>} />
        <Route path="/thank-you"    element={<ThankYou />} />
        <Route path="*"             element={<><Seo id="home" /><Home go={go} /></>} />
      </Routes>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}